import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { CheckCheck, Save } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { useMember } from "../lib/member";
import { PageHeader, Empty, useVendors } from "../components/shared";
import { Button } from "../components/ui/button";
import { DatePicker } from "../components/DateField";
import { AttendanceClashes } from "../components/AttendanceClashes";
import { saveAttendance, QUEUED_MSG } from "../lib/offline";
import { offlineNow } from "../lib/idb";

const OPTS = [["P", "Present", "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"], ["A", "Absent", "bg-rose-500/15 text-rose-400 border-rose-500/30"],
  ["H", "Half day", "bg-amber-500/15 text-amber-400 border-amber-500/30"], ["L", "Leave", "bg-cyan-500/15 text-cyan-400 border-cyan-500/30"]];

export default function Attendance() {
  const api = useApi();
  const { t } = useMember();
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [siteId, setSiteId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const { data: sites } = useFetch("/sites");
  const { data: vendors } = useVendors();
  const q = new URLSearchParams({ day, ...(siteId && { site_id: siteId }), ...(vendorId && { vendor_id: vendorId }) });
  const path = `/attendance?${q}`;
  const { data: raw, reload, setData, loading } = useFetch(path);
  const { data: roster } = useFetch(!raw && !loading && offlineNow() ? "/workers" : null);
  const data = useMemo(() => raw || (roster && roster.filter((w) => w.is_active !== false && (!siteId || w.site_id === siteId) && (!vendorId || w.vendor_id === vendorId))
    .map((w) => ({ id: w.id, worker_full_name: w.worker_full_name, skill_category: w.skill_category, vendor_id: w.vendor_id, site_id: w.site_id, status: null }))
    .sort((a, b) => a.worker_full_name.localeCompare(b.worker_full_name))), [raw, roster, siteId, vendorId]);
  const [marks, setMarks] = useState({});
  const [extra, setExtra] = useState({});
  const [shift, setShift] = useState("G");
  const { data: shifts } = useFetch("/mis/shifts");
  useEffect(() => {
    setMarks(Object.fromEntries((data || []).filter((w) => w.status).map((w) => [w.id, w.status])));
    setExtra(Object.fromEntries((data || []).filter((w) => w.status).map((w) => [w.id, { shift: w.shift || "G", ot_hours: w.ot_hours || 0 }])));
  }, [data]);
  const setX = (id, k, v) => setExtra((x) => ({ ...x, [id]: { shift, ot_hours: 0, ...x[id], [k]: v } }));

  const save = async () => {
    if (!Object.keys(marks).length) return toast.error("Mark at least one worker");
    const ex = Object.fromEntries(Object.keys(marks).map((id) => [id, { shift: extra[id]?.shift || shift, ot_hours: Number(extra[id]?.ot_hours) || 0 }]));
    try {
      const r = await saveAttendance(api, path, data, day, marks, ex);
      if (r.queued) { toast.message(QUEUED_MSG); setData(data.map((w) => ({ ...w, status: marks[w.id] || w.status }))); }
      else { toast.success(`Saved ${r.saved} records`); reload(); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const counts = OPTS.map(([k]) => Object.values(marks).filter((v) => v === k).length);

  return (
    <div>
      <PageHeader eyebrow="Daily muster" title={t("Attendance")} desc="Half days count as 0.5 and leave as unpaid when payroll is run from attendance.">
        <Button variant="outline" onClick={() => setMarks(Object.fromEntries((data || []).map((w) => [w.id, "P"])))} data-testid="mark-all-present-btn"
          className="border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white"><CheckCheck size={15} className="mr-2" />{t("Mark all present")}</Button>
        <Button onClick={save} data-testid="attendance-save-btn" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold"><Save size={15} className="mr-2" />{t("Save")}</Button>
      </PageHeader>
      <AttendanceClashes onDone={reload} />
      <div className="c-surface p-4 grid sm:grid-cols-4 gap-3 mb-4 c-rise">
        <DatePicker value={day} onChange={setDay} max={today} clearable={false} fromYear={2015} testid="attendance-date-input" />
        <select className="c-input" value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="attendance-site-filter">
          <option value="">All sites</option>{(sites || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <select className="c-input" value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="attendance-vendor-filter">
          <option value="">All contractors</option>{(vendors || []).map((v) => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}</select>
        <select className="c-input" value={shift} onChange={(e) => setShift(e.target.value)} title="Default shift for new marks" data-testid="attendance-default-shift">
          {(shifts || [{ code: "G", name: "General" }]).map((s) => <option key={s.code} value={s.code}>Shift: {s.name}</option>)}</select>
      </div>
      <div className="flex flex-wrap gap-4 mb-4 text-xs font-mono text-slate-400" data-testid="attendance-counts">
        {OPTS.map(([k, l], i) => <span key={k}>{t(l)}: <b className="text-white">{counts[i]}</b></span>)}<span>Unmarked: <b className="text-white">{(data?.length || 0) - Object.keys(marks).length}</b></span>
      </div>
      <div className="c-surface overflow-x-auto c-rise">
        <table className="c-table w-full">
          <thead><tr><th>Worker</th><th>Category</th><th>Status</th><th>Shift</th><th>OT hours</th></tr></thead>
          <tbody>{(data || []).map((w) => (
            <tr key={w.id} data-testid="attendance-row">
              <td className="font-medium">{w.worker_full_name}</td><td className="text-xs text-slate-400">{w.skill_category}</td>
              <td><div className="flex gap-1.5">{OPTS.map(([k, l, cls]) => (
                <button key={k} onClick={() => setMarks((m) => ({ ...m, [w.id]: k }))} data-testid={`att-${k}-${w.id}`} data-on={marks[w.id] === k ? "1" : undefined}
                  className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${marks[w.id] === k ? cls : "border-[#1F2A3E] text-slate-500 hover:text-white"}`}>{t(l)}</button>))}
              </div></td>
              <td><select className="c-input !py-1 !text-xs w-20" value={extra[w.id]?.shift || shift} onChange={(e) => setX(w.id, "shift", e.target.value)} data-testid={`att-shift-${w.id}`}>
                {(shifts || [{ code: "G" }]).map((s) => <option key={s.code} value={s.code}>{s.code}</option>)}</select></td>
              <td><input type="number" min="0" max="12" step="0.5" className="c-input !py-1 !text-xs w-16" disabled={!["P", "H"].includes(marks[w.id])}
                value={extra[w.id]?.ot_hours || ""} onChange={(e) => setX(w.id, "ot_hours", e.target.value)} data-testid={`att-ot-${w.id}`} /></td>
            </tr>))}</tbody>
        </table>
        {data?.length === 0 && <Empty text="No active workers for this filter." />}
        {!data && !loading && offlineNow() && <Empty text="No signal, and this phone has no saved worker list yet. Open Workers once with signal." testid="attendance-offline-empty" />}
      </div>
    </div>
  );
}
